import './Game.css';

import React, {Component} from 'react';
import classnames from 'classnames';
import cloneDeep from 'lodash.clonedeep';

// Components
import Deck from '../Deck/Deck';
import Side from '../Side/Side';
import SideHint from '../SideHint';
import Popup from '../Popup/Popup';
import Message from '../Message/Message';
import CTA from '../CTA/CTA';
import Logo from '../Logo/Logo';
import Intro from '../Intro/Intro';

// Data
import initialCards from './GameData';
import STATUS from './constants';

// TODO move this in one spot
// Style settings when added to either side
const scale = 0.48;

export default class Game extends Component {
  constructor(props) {
    super(props);

    this.defaultOptions = {
      status: STATUS.LOGO,
      positiveCounter: 0,
      negativeCounter: 0,
      classes: '',
      showPopup: false,
      isCorrect: true,
      demo: {
        correctClass: '', // add swipe-and-fade class on intro_demo
        incorrectClass: '',
        instructionStep: 0,
      },
    }

    this.state = Object.assign({}, this.defaultOptions, {
      cards: this.shuffle(initialCards()),
    });
  }

  /**
   * Restart the game
   */
  startGame() {
    // Reset state
    const mergeSettings = Object.assign({}, this.defaultOptions, {
      cards: this.shuffle(initialCards()),
      status: STATUS.GAME_START, // start the game
    });

    this.setState(mergeSettings);
  }

  /**
   * Play the intro
   */
  playIntro() {
    const cardsPool = initialCards();
    const introCards = [cardsPool[7], cardsPool[0], cardsPool[4], cardsPool[1], cardsPool[5], cardsPool[2], cardsPool[6], cardsPool[3]];

    this.setState({
      cards: introCards,
      status: STATUS.INTRO,
      demo: {
        correctClass: '',
        incorrectClass: '',
        instructionStep: 0,
      },
    });
  }

  /**
   * Shuffle cards
   * @param {*} cards
   */
  shuffle(cards) {
    const newCards = [...cards];
    for (let i = newCards.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [newCards[i], newCards[j]] = [newCards[j], newCards[i]];
    }
    return newCards;
  }

  /**
   * User grabs the card
   * @param {*} index
   */
  handlePanStart(index) {
    const cards = this.state.cards;

    // Turn off transition
    cards[index].classes = 'is-moving';

    // Update cards
    this.setState({
      cards
    })
  }

  /**
   * User drops the card
   * @param {*} event
   */
  handlePanEnd(index, event) {
    const cards = this.state.cards;
    const card = cards[index];
    let showPopup = this.state.showPopup;
    let isCorrect = true;

    // Remove positive or negative to the Game component
    // Remove it using classList property
    // because the classes are added this way
    event.target.closest('.Game').classList.remove('positive');
    event.target.closest('.Game').classList.remove('negative');

    // Update current card
    // Add transition back to the cards
    card.classes = '';
    const deltaX = event.deltaX;

    if (Math.abs(deltaX) < 80) {
      this.keepTheCard(card, event);

      // Do not show popup
      showPopup = false;
    } else {
      const sign = deltaX > 0 ? '' : '-';

      // If card is dragged to the positive side
      // and the card is positive
      // Or if the card is dragged to the negative side
      // and the card is negative
      if ((deltaX > 0 && card.behavior !== 'positive')
          || (deltaX < 0 && card.behavior !== 'negative')) {
        // Wrong move
        this.keepTheCard(card, event);

        // Show popup message
        isCorrect = false;
      }

      // Run common function on success
      if (isCorrect) {
        this.moveTheCard(card, sign);
      }

      // Show popup
      showPopup = true;
    }

    // Update Game
    this.setState({
      cards,
      isCorrect,
      showPopup
    })
  }

  /**
   * Move the card back to the deck
   * @param {*} card
   */
  keepTheCard(card, event) {
    // Update transform value
    // since we are updating the
    // transform value on pan
    event.target.style.transform = '';
  }

  /**
   * Move the card to the side
   */
  moveTheCard(card, sign) {
    // Calculate cards position
    const endX = `${sign}133%`;
    const endY = `-11%`;

    card.style = {
      transform: `translate(${endX}, ${endY}) scale(${scale})`,
    }

    // Set back to lock so the user won't be
    // able to move it around
    card.locked = true;

    // Set to pinned, used for filtering the cards not in the deck
    card.pinned = true;

    // Set the counter
    // to update the transform origin
    if (card.behavior === 'positive') {
      this.setState((prevState) => {
        card.counter = prevState.positiveCounter + 1;
        return {
          positiveCounter: card.counter,
        };
      });
    } else  {
      this.setState((prevState) => {
        card.counter = prevState.negativeCounter + 1;
        return {
          negativeCounter: card.counter,
        };
      });
    }
  }

  /**
   * Callback for Popup component
   * If there is more than one card left on the deck
   * otherwise don't do anything
   * until the player selects the correct side
   */
  popupCallback(isExit) {
    if (isExit) {
      // If no cards are left on the deck
      if (this.state.cards.filter(card => !card.pinned).length === 0) {
        this.setState({
          status: STATUS.GAME_END,  // Fade out the cards
        })
      }
      return
    }
    // Flip the card
    // at the top of the stack
    if (this.state.isCorrect) {
      this.flipNextCard();
    } else {
      const deckCards = this.state.cards.filter((card) => !card.pinned);

      // Reverse active card
      if (deckCards.length > 1) {
        deckCards[deckCards.length - 1].flipped = false;
        deckCards[deckCards.length - 1].reversed = true;
        deckCards[deckCards.length - 1].locked = true;
      } else {
        deckCards[0].locked = false;
      }
    }

    this.setState({
      showPopup: false,
      status: STATUS.GAME_PLAY,
    })
  }

  /**
   * Callback on animation end for the card
   */
  flipCallback(event, index) {
    const cards = cloneDeep(this.state.cards);
    const card = cards[index];
    let correctClass = '';
    let incorrectClass = '';

    // In intro, trigger next animation after either of the first two cards are
    // flipped
    if (event.animationName === 'flipNoScale' || event.animationName === 'revealNoTransformIe') {
      if (this.state.status !== STATUS.INTRO_DEMO || (index !== 7 && index !== 6)) {
        return;
      }

      // Pause a moment so the instruction can be read
      setTimeout(() => {
        if (index === 7) {
          // Swipe to right
          card.classes = 'swipe-left';

          // Scale and fade out green bubble
          correctClass = 'scale-and-fade';
        } else  {
          // Swipe to left
          card.classes = 'swipe-right';

          // Scale and fade out red bubble
          incorrectClass = 'scale-and-fade';
        }

        this.setState((prevState) => ({
          cards,
          demo: Object.assign({}, prevState.demo, {
            correctClass,
            incorrectClass,
          }),
        }))
      }, 1.5e3);

      return;
    }

    // INTRO_DEMO Card animation
    if (event.animationName === 'swipe-left' && this.state.status === STATUS.INTRO_DEMO && index === 7) {
      // Remove top card
      cards.pop();

      // After flipping the card on intro
      cards[cards.length - 1].flipped = true;

      // Update instructions
      setTimeout(() => {
        this.setState((prevState) => ({
          demo: Object.assign({}, prevState.demo, {
            instructionStep: 2,
          }),
        }))
      }, 1e3)
    }

    // INTRO_DEMO Card animation
    if (event.animationName === 'swipe-right' && this.state.status === STATUS.INTRO_DEMO && index === 6) {
      // Show final instruction for a moment, then show logo
      this.setState((prevState) => ({
        demo: Object.assign({}, prevState.demo, {
          instructionStep: 3,
        }),
      }), () => {
        setTimeout(() => {
          // Remove top card
          cards.pop();

          // Quickly distribute all remaining cards to the sides
          for (let i = 0; i < cards.length; i++) {
            const card = cards[i];
            card.classes = card.behavior === 'negative' ? 'swipe-left' : 'swipe-right';
            card.classes += ` anim-delay-${cards.length - i}`;
          }

          this.setState({
            cards,
          })
          setTimeout(() => {
            this.setState({
              cards: this.shuffle(initialCards()),
              status: STATUS.LOGO,
            });
          }, 2e3);
        }, 1e3)
      });

      return;
    }

    if (cards.length) {
      // Move the first card to the bottom of the stack
      if (event.animationName === 'flipBack' || event.animationName === 'unrevealNoTransformIe') {
        // Remove the transform value in the CardDeck
        // event.currentTarget.classList.add('rotate-out');
        card.classes = 'rotate-out';
      }

      if (event.animationName === 'rotate-out') {
        card.classes = 'rotate-in';
      }

      if (event.animationName === 'rotate-in') {
        // Reset card
        card.flipped = false;
        card.reversed = false;
        card.locked = true;
        card.classes = '';

        // Move the item to the start of the array
        cards.unshift(cards.splice(index, 1)[0]);

        // Flip next card
        cards[index].flipped = true;
        cards[index].locked = false;
      }

      this.setState((prevState) => ({
        cards,
        demo: Object.assign({}, prevState.demo, {
          correctClass,
          incorrectClass,
        }),
      }))
    }
  }

  /**
   * Callback for rendering the deck
   */
  deckCallback(isExit) {
    // After cards slide in
    if (!isExit) {
      // Flip the card at the top of the deck
      this.flipNextCard();

      // Render the cards again
      // but without animation
      this.setState({
        status: STATUS.GAME_PLAY,
      })
    } else {
      // On Exit callback
      // Show the final message
      // And move the sides outside of the screen
      this.setState({
        status: STATUS.GAME_EXIT
      })
    }
  }

  /**
   * Intro callback
   * Cards slide in from Deck component,
   * after the cards slide in,
   * this function is called.
   */
  introCallback() {
    // Flip the card at the top of the deck
    this.flipNextCard();

    this.setState({
      status: STATUS.INTRO_DEMO,
    }, () => {
      // Delay a moment before showing the instruction to draw the eye to the
      // instruction rather than the card
      setTimeout(() => {
        this.setState({
          demo: Object.assign({}, this.state.demo, {
            instructionStep: 1,
          }),
        });
      }, 1e3);
    });
  }

  /**
   * Message callback
   */
  messageCallback() {
    this.setState({
      status: STATUS.CTA
    })
  }

  /**
   * Flip the top card on the deck
   */
  flipNextCard() {
    const deckCards = this.state.cards.filter((card) => !card.pinned);

    if (deckCards.length) {
      deckCards[deckCards.length-1].flipped = true;
      deckCards[deckCards.length-1].locked = false;
    }
  }

  // Render
  render() {
    const classes = classnames(
      'Deck-container col-12 flex flex--justify-c flex--align-fe',
      {'blur' : this.state.showPopup},
    )

    return (
      <div style={{height: '100%', position: 'relative'}}>
        {/* Intro */}
        <Intro status={this.state.status} correctClass={this.state.demo.correctClass} incorrectClass={this.state.demo.incorrectClass} instructionStep={this.state.demo.instructionStep} />

        <div className="Game">
          {/* Logo */}
          <Logo status={this.state.status} startGame={this.startGame.bind(this)} playIntro={this.playIntro.bind(this)} />

          {/* Deck */}
          <div className={classes}>
            <Side behavior="negative"
                  status={this.state.status} />

            <Deck cards={this.state.cards}
                  status={this.state.status}
                  callback={this.deckCallback.bind(this)}
                  introCallback={this.introCallback.bind(this)}
                  flipCallback={this.flipCallback.bind(this)}
                  handlePanEnd={this.handlePanEnd.bind(this)}
                  handlePanStart={this.handlePanStart.bind(this)} />

            <Side behavior="positive"
                  status={this.state.status} />

              {this.state.status === STATUS.GAME_PLAY && <React.Fragment>
              <SideHint behavior="positive" />
              <SideHint behavior="negative" />
            </React.Fragment>}
          </div>

          {/* Popup */}
          <Popup isCorrect={this.state.isCorrect} showPopup={this.state.showPopup} callback={this.popupCallback.bind(this)} />

          {/* Message */}
          <Message status={this.state.status} callback={this.messageCallback.bind(this)} />

          {/* CTA - Well played */}
          <CTA status={this.state.status} callback={this.startGame.bind(this)} />
        </div>
      </div>
    )
  }
}



// WEBPACK FOOTER //
// ./src/components/rlgl/Game/Game.js